import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCachedResource } from "../hooks/useCachedResource";
import { trialBalance } from "../api/accounting";
import { AcctShell } from "../features/acct/AcctShell";
import { NeuCard } from "../components/neu/Neu";
import { DateInput } from "../components/neu/DateInput";
import { localName } from "../lib/localName";

export default function AcctTrialBalancePage() {
  const { t, i18n } = useTranslation();
  const ar = i18n.language === "ar";
  const [range, setRange] = useState({ date_from: "", date_to: "" });
  const DEF = { rows: [], total_debit: "0.00", total_credit: "0.00", balanced: true };
  const { data: raw } = useCachedResource(`acct-tb-${range.date_from}-${range.date_to}`, () => trialBalance(range).catch(() => DEF), [range]);
  const data = raw || DEF;

  return (
    <AcctShell titleKey="trialBalance">
      <div className="flex flex-wrap gap-3">
        {["date_from", "date_to"].map((key) => (
          <DateInput
            key={key}
            className="w-40"
            label={t(`acct.${key}`)}
            testId={`tb-${key}`}
            value={range[key]}
            onChange={(v) => setRange((prev) => ({ ...prev, [key]: v }))}
          />
        ))}
      </div>

      <NeuCard className="overflow-x-auto p-4" data-testid="trial-balance">
        <table className="w-full min-w-[520px] text-sm">
          <thead>
            <tr className="text-[11px] text-muted-foreground">
              <th className="p-2 text-start">{t("acct.account")}</th>
              <th className="p-2 text-end">{t("acct.debit")}</th>
              <th className="p-2 text-end">{t("acct.credit")}</th>
              <th className="p-2 text-end">{t("acct.balance")}</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.code} className="border-t border-border/40" data-testid={`tb-row-${row.code}`}>
                <td className="p-2">
                  <span className="font-mono text-xs font-bold" dir="ltr">{row.code}</span>{" "}
                  {localName(row)}
                </td>
                <td className="p-2 text-end font-mono" dir="ltr">{row.debit}</td>
                <td className="p-2 text-end font-mono" dir="ltr">{row.credit}</td>
                <td className="p-2 text-end font-mono" dir="ltr">{row.balance}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border/60 font-bold">
              <td className="p-2">{t("acct.totals")}</td>
              <td className="p-2 text-end font-mono" dir="ltr" data-testid="tb-total-debit">{data.total_debit}</td>
              <td className="p-2 text-end font-mono" dir="ltr" data-testid="tb-total-credit">{data.total_credit}</td>
              <td className={`p-2 text-end text-xs ${data.balanced ? "text-success" : "text-destructive"}`} data-testid="tb-balanced">
                {data.balanced ? t("acct.balanced") : t("acct.unbalanced")}
              </td>
            </tr>
          </tfoot>
        </table>
      </NeuCard>
    </AcctShell>
  );
}
